// UnsentValuations.jsx — valuations started on sentavus.com and not sent (owner 2026-10-07: "7 yes show it").
//
// The valuation form autosaves everything that is filled (core_fill.js /progress `autosave`), but only a submitted one
// becomes a property and a lead. These are the visitors who saw the estimate (or stopped earlier) and left — there is no
// property yet, so they are not lead cards: one collapsed block above the Leads feed, closed by default and absent when
// the list is empty. The phone follows the Leads rule: the list only says one was typed; "show" asks the server, which
// records who looked (core_unsent_valuations.js /:id/phone).
//
// 2026-10-28 (owner: "it's stuck at 50" / "I can't open it to see what users have filled out"): the title counts the
// server's TOTAL, not the page, with "show more" for the rest; a row opens a window with every saved answer under its
// question's wording, and "Edit and send" opens the real sentavus.com form on those answers (the server mints the link,
// see core_unsent_valuations.js /finish for why it is a new link) — finished there, it is a property + lead the normal way.

import { useEffect, useState } from 'react'
import { getLanguage, t } from '../../lib/i18n.js'
import { getUnsentValuations, getUnsentValuation, revealUnsentValuationPhone, finishUnsentValuation } from '../../db/core.js'
import { Modal } from './primitives.jsx'

const PAGE = 50
const SITE = 'https://sentavus.com'

const ago = iso => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000))
  return m < 60 ? `${m} ${t('desk_unsent_min')}` : m < 1440 ? `${Math.round(m / 60)} ${t('desk_unsent_h')}` : `${Math.round(m / 1440)} ${t('desk_unsent_d')}`
}

function PhoneButton({ id }) {
  const [phone, setPhone] = useState(null)
  const [busy, setBusy] = useState(false)
  return phone
    ? <a className="font-medium text-primary" href={`tel:${phone}`}>{phone}</a>
    : <button type="button" disabled={busy} className="font-medium text-primary underline underline-offset-2"
        onClick={async e => { e.stopPropagation(); setBusy(true); try { setPhone((await revealUnsentValuationPhone(id)).phone || '—') } catch { setPhone('—') } setBusy(false) }}>
        {t('desk_unsent_show_phone')}
      </button>
}

function Row({ v, districtName, onOpen }) {
  const a = v.answers || {}
  const facts = [
    v.kind, v.intent, districtName(v.district_code), v.address,
    a.rooms != null ? `${a.rooms} ${t('desk_unsent_rooms')}` : null,
    a.area != null ? `${typeof a.area === 'object' ? a.area.value : a.area} m²` : null,
    a.land_area != null ? `${t('desk_unsent_plot')} ${typeof a.land_area === 'object' ? `${a.land_area.value} ${a.land_area.unit || ''}` : a.land_area}` : null,
    a.floor != null ? `${t('desk_unsent_floor')} ${a.floor}${a.floors_total ? `/${a.floors_total}` : ''}` : null,
    a.condition || null, a.parking || null,
  ].filter(Boolean)
  const shown = Object.keys(a).filter(k => !['rooms', 'area', 'land_area', 'floor', 'floors_total', 'condition', 'parking'].includes(k)).length
  return (
    <li className="border-b border-border/60 last:border-b-0">
      <div role="button" tabIndex={0} className="cursor-pointer rounded-md px-1 py-2 hover:bg-muted/60"
        onClick={() => onOpen(v)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(v) } }}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-sm text-foreground">{facts.join(' · ') || t('desk_unsent_empty_answers')}</span>
          <span className="text-xs text-muted-foreground">{ago(v.saved_at || v.started_at)}{v.step ? ` · ${v.step}` : ''}</span>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {shown > 0 && <span>{t('desk_unsent_more_answers').replace('{n}', shown)}</span>}
          {v.name && <span className="text-foreground">{v.name}</span>}
          {v.has_phone && <PhoneButton id={v.id} />}
          {!v.name && !v.has_phone && <span>{t('desk_unsent_no_contact')}</span>}
          {v.finishing && <span className="text-amber-600 dark:text-amber-400">{t('desk_unsent_finishing')}</span>}
          <span className="ml-auto font-medium text-primary">{t('desk_unsent_open')} ›</span>
        </div>
      </div>
    </li>
  )
}

// one answer as words: the question's option / feature name for a code, yes / no / not sure for the tristates
function answerText(v, codes, unit) {
  if (v == null || v === '') return null
  if (v === true) return t('desk_unsent_yes')
  if (v === false) return t('desk_unsent_no')
  if (v === 'unsure') return t('desk_unsent_unsure')
  if (Array.isArray(v)) return v.map(x => answerText(x, codes)).filter(Boolean).join(', ')
  if (typeof v === 'object') {
    if ('value' in v) return [answerText(v.value, codes), v.unit].filter(Boolean).join(' ')
    if ('v' in v) return answerText(v.v, codes)
    return Object.entries(v).map(([k, x]) => `${codes[k] || k}: ${answerText(x, codes)}`).join('; ')
  }
  if (typeof v === 'string') return codes[v] || v
  return unit ? `${v} ${unit === 'm2' ? 'm²' : unit}` : String(v)
}

// a question the site's form asks itself (not in the intake catalogue): the Desk's own wording, numbered for the
// repeated room areas (bedroom_area_2 → "Bedroom area 2"); the raw key only when neither knows it
function formLabel(k) {
  const m = k.match(/^(.*)_(\d+)$/)
  const key = `desk_unsent_q_${m ? m[1] : k}`
  const w = t(key)
  return w && w !== key ? (m ? `${w} ${m[2]}` : w) : k.replace(/_/g, ' ')
}

function ValuationModal({ id, districtName, onClose }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    let live = true
    getUnsentValuation(id, getLanguage()).then(r => { if (!live) return; if (r && r.valuation) setData(r); else setErr(t('desk_unsent_load_failed')) })
      .catch(() => { if (live) setErr(t('desk_unsent_load_failed')) })
    return () => { live = false }
  }, [id])
  const finish = async () => {
    // opened BEFORE the await: a tab opened after it is a popup the browser blocks
    const w = window.open('', '_blank')
    setBusy(true); setErr(null)
    try {
      const r = await finishUnsentValuation(id)
      if (!r || !r.token) throw new Error('no token')
      const url = `${SITE}/${getLanguage() === 'en' ? 'en' : 'ka'}/fill/property/${r.token}`
      if (w) { w.opener = null; w.location.href = url } else window.location.assign(url)
      setData(d => d && { ...d, valuation: { ...d.valuation, finishing: true } })
    } catch { if (w) w.close(); setErr(t('desk_unsent_finish_failed')) }
    setBusy(false)
  }
  const v = data?.valuation
  const codes = data?.codes || {}, labels = data?.labels || {}, order = data?.order || {}, units = data?.units || {}
  const rows = v ? Object.entries(v.answers || {})
    .map(([k, x]) => ({ k, label: labels[k] || formLabel(k), text: answerText(x, codes, units[k]) }))
    .filter(r => r.text)
    .sort((a, b) => (order[a.k] ?? 9999) - (order[b.k] ?? 9999)) : []
  const place = v ? [
    [codes[v.kind] || v.kind, codes[v.intent] || v.intent, codes[v.subtype] || v.subtype].filter(Boolean).join(' · '),
    [v.city, districtName(v.district_code), v.address || [v.street, v.street_number].filter(Boolean).join(' '), v.complex_name].filter(Boolean).join(' · '),
  ].filter(Boolean) : []
  return (
    <Modal title={t('desk_unsent_title')} subtitle={v ? `${ago(v.saved_at || v.started_at)}${v.step ? ` · ${t('desk_unsent_step')} ${v.step}` : ''}` : null} onClose={onClose}>
      <div className="space-y-4 p-4 text-sm">
        {!data && !err && <p className="text-muted-foreground">…</p>}
        {err && <p className="text-red-600">{err}</p>}
        {v && <>
          <section>
            {place.map((x, i) => <p key={i} className={i ? 'text-muted-foreground' : 'font-medium'}>{x}</p>)}
          </section>
          <section>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('desk_unsent_contact')}</h3>
            <div className="flex flex-wrap items-center gap-3">
              {v.name && <span>{v.name}</span>}
              {v.has_phone && <PhoneButton id={v.id} />}
              {!v.name && !v.has_phone && <span className="text-muted-foreground">{t('desk_unsent_no_contact')}</span>}
            </div>
          </section>
          <section>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('desk_unsent_answers')} ({rows.length})</h3>
            {rows.length ? (
              <dl>
                {rows.map(r => (
                  <div key={r.k} className="flex justify-between gap-3 border-b border-border/50 py-1.5">
                    <dt className="text-muted-foreground">{r.label}</dt>
                    <dd className="text-right text-foreground">{r.text}</dd>
                  </div>
                ))}
              </dl>
            ) : <p className="text-muted-foreground">{t('desk_unsent_empty_answers')}</p>}
            {v.notes && <p className="mt-2 whitespace-pre-wrap rounded-md bg-muted/50 p-2">{v.notes}</p>}
          </section>
          <section className="sticky bottom-0 -mx-4 border-t border-border bg-card px-4 pb-1 pt-3">
            <p className="mb-2 text-xs text-muted-foreground">{t('desk_unsent_finish_hint')}</p>
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" disabled={busy} onClick={finish}
                className="rounded-md bg-primary px-3 py-1.5 font-medium text-primary-foreground disabled:opacity-50">
                {t('desk_unsent_finish')} ↗
              </button>
              {v.finishing && <span className="text-xs text-amber-600 dark:text-amber-400">{t('desk_unsent_finishing')}</span>}
            </div>
          </section>
        </>}
      </div>
    </Modal>
  )
}

export function UnsentValuations({ districts }) {
  const [rows, setRows] = useState(null)
  const [total, setTotal] = useState(0)
  const [open, setOpen] = useState(false)
  const [more, setMore] = useState(false)
  const [openId, setOpenId] = useState(null)
  useEffect(() => {
    let live = true
    getUnsentValuations({ limit: PAGE }).then(r => { if (live) { setRows(r.valuations); setTotal(r.total) } }).catch(() => { if (live) setRows([]) })
    return () => { live = false }
  }, [])
  if (!rows || !rows.length) return null
  const districtName = code => {
    if (!code) return null
    const d = (districts || []).find(x => x.code === code)
    return d ? (d.name_ka || d.name_en || code) : code
  }
  const loadMore = async () => {
    setMore(true)
    try {
      const r = await getUnsentValuations({ limit: PAGE, offset: rows.length })
      const seen = new Set(rows.map(x => x.id))
      setRows([...rows, ...r.valuations.filter(x => !seen.has(x.id))]); setTotal(r.total)
    } catch { /* the button stays; a second press retries */ }
    setMore(false)
  }
  const left = Math.max(0, total - rows.length)
  return (
    <section className="mb-3 rounded-lg border border-border bg-card px-3 py-2">
      <button type="button" className="flex w-full items-center justify-between text-left" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className="text-sm font-medium text-foreground">{t('desk_unsent_title')} ({total})</span>
        <span className="text-xs text-muted-foreground">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <>
          <p className="mt-1 text-xs text-muted-foreground">{t('desk_unsent_hint')}</p>
          <ul className="mt-1">{rows.map(v => <Row key={v.id} v={v} districtName={districtName} onOpen={x => setOpenId(x.id)} />)}</ul>
          {left > 0 && (
            <button type="button" disabled={more} onClick={loadMore}
              className="mt-2 w-full rounded-md border border-border py-1.5 text-xs font-medium text-primary hover:bg-muted disabled:opacity-50">
              {t('desk_unsent_load_more').replace('{n}', Math.min(PAGE, left))}
            </button>
          )}
        </>
      )}
      {openId && <ValuationModal id={openId} districtName={districtName} onClose={() => setOpenId(null)} />}
    </section>
  )
}
